import * as activation from './components'
import _ from 'lodash'
import { Suspense } from 'react'
import { isValidElementType } from 'react-is'
import { hasExposedNamespace, hasMicroApp, lazyImportComponent, LoadingPlaceholder } from '@sparky/framework'
import type { VFC, ComponentType, ReactElement } from 'react'
import type { CommonStoreState } from './store'

type AddonLicenseTypeEnums = {
  [key: string]: string
}
export type Addon = {
  app_id: string,
  display_name: string,
  licenseTypeTitle?: string,
  autoActivate?: boolean,
  licenseTypeEnums?: AddonLicenseTypeEnums,
  required_x_signature?: boolean,
}

export type ManifestFlags = {
  hash_required: Boolean
  cdl_required?: Boolean
  amend_license?: Boolean
  show_license_divider?: Boolean
  show_purchased_size_as_qty?: Boolean
  sub_domain_required?: Boolean
  migrate_cdl?: Boolean,
  addons?: Addon[],
  has_license_packages?: boolean,
}

export enum PrimaryAppIds {
  STRATA_INSIGHTS = 'strata_insights',
  STRATA_INSIGHTS_FREE = 'strata_insights_free',
  SSPM = 'sspm',
  BACKBONE = 'mssp_backbone',
  PRISMA_ACCESS = 'prisma_access_edition',
  ZINGBOX = 'zingbox',
  NG_CASB = 'ng_casb',
  SDWAN = 'cgx',
  LOGGING_SERVICE = 'logging_service',
  SAAS = 'aperture',
  PROFILE = 'profile',
  ADEM = 'ademx',
  RBI = 'rbi'
}
export interface ActivationManifest {
  activation_id: string
  description: string
  primary_app_id: PrimaryAppIds
  activation_screens: ScreenComponentDef[]
  extra?: Record<string, any>
  flags?: ManifestFlags
}

export interface ComponentDef {
  key: string
  component: string | ComponentType
  props: {
    children?: ComponentDef | ComponentDef[]
    [key: string]: any
  }
}

export type ScreenProps = {
  title: string
  description: string
  primaryButton: {
    text: string
  }
  children?: ComponentDef | ComponentDef[]
  [key: string]: any
}
export type ScreenComponentProps = ScreenProps & {
  state: CommonStoreState
}

export interface ScreenComponentDef extends ComponentDef {
  props: ScreenProps
}

export const GLOBAL_EXPOSE_KEY = 'global'

export const componentsReg = { activation } // temp mocking the registry

export const NotFoundOrInvalid: VFC<{}> = () => <>Component Not Found or Invalid.</>

const getSuspenseComponent = (key: string): ComponentType | undefined => {
  const [rootKey, ...keys] = key.split('.')
  if (hasExposedNamespace?.(rootKey) ?? (rootKey === GLOBAL_EXPOSE_KEY || hasMicroApp(rootKey))) {
    const LazyComp = lazyImportComponent(rootKey, keys.join('.'))
    const SuspenseComponent = (props: any) => <Suspense fallback={<LoadingPlaceholder />}><LazyComp {...props} /></Suspense>
    return SuspenseComponent
  }
}

export const cachedComponentKeyMap = new Map<string, ComponentType>() // export for unit testing
const cachedGetComponent = (key: string): ComponentType => {
  const cached = cachedComponentKeyMap.get(key)
  if (cached) {
    return cached
  }
  const Component: ComponentType | undefined = _.get(componentsReg, key) || getSuspenseComponent(key)
  if (isValidElementType(Component)) {
    cachedComponentKeyMap.set(key, Component)
    return Component
  }
  return NotFoundOrInvalid
}

export const render = <T = any>(config: ComponentDef[] | ComponentDef, commonProps: T): ReactElement => {
  if (Array.isArray(config)) {
    return <>{(config as ComponentDef[]).map(configElm => render(configElm, commonProps))}</>
  }
  const Component = _.isString(config.component) ? cachedGetComponent(config.component) : config.component
  if (!isValidElementType(Component) || Component === NotFoundOrInvalid) {
    return <NotFoundOrInvalid />
  }
  const { children, ...props } = config.props
  if (Array.isArray(children) && children?.length) {
    props.children = render(children, commonProps)
  }
  props.key = config.key
  return <Component {...props} {...commonProps} />
}

export const Renderer: VFC<{ config: ComponentDef[] | ComponentDef, commonProps: any }> = ({ config, commonProps }) => render(config, commonProps)

export default render
